import { ContentsTable } from '@/components/ContentsTable/ContentsTable';
import { getBaseColumns } from '@/components/ContentsTable/columnDefinitions';
import { createComparePageRenderName } from './createComparePageRenderName';
import { useFetchFolderContents } from '@/hooks/api/folders/useFetchFolderContents';
import { useGetFolder } from '@/hooks/api/folders/useGetFolder';
import { useGetRootFolder } from '@/hooks/api/folders/useGetRootFolder';
import { useMetadataColumnVisibility } from '@/hooks/useMetadataColumnVisibility';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useGetMetadataFieldsWithData } from '@/hooks/api/folders/useGetMetadataFieldsWithData';
import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export const CompareTable = () => {
  const [searchParameters, setSearchParameters] = useSearchParams();
  const folderId = searchParameters.get('folderId') ?? undefined;

  const { isEmpty: isRootFolderEmpty, isLoading: isRootFolderLoading } = useGetRootFolder({
    enabled: !folderId,
  });
  const { fetchData, error: folderError } = useFetchFolderContents({
    folderId: folderId,
  });
  const { folder, isLoading: isFolderLoading } = useGetFolder({
    folderId: folderId,
  });
  const { metadataFieldNames, isLoading: isLoadingFieldNames } = useGetMetadataFieldsWithData({
    folderId,
    enabled: true,
  });
  const { permissions } = useGetCurrentUser();

  const { metadataColumns, columnVisibilityModel, onColumnVisibilityModelChange, isMetadataLoading } =
    useMetadataColumnVisibility({
      metadataFieldNames,
      isLoadingFieldNames,
      canEditMetadata: permissions.canModifyAssetMetadata,
      contextKey: folderId || 'root',
    });

  const isFolderEmpty =
    !isFolderLoading &&
    !isRootFolderLoading &&
    (folderId ? (folder?.assetsCount ?? 0) + (folder?.foldersCount ?? 0) === 0 : isRootFolderEmpty);

  const handleFolderClick = useCallback(
    (newFolderId: string) => {
      const newParameters = new URLSearchParams(searchParameters);
      newParameters.set('folderId', newFolderId);
      setSearchParameters(newParameters);
    },
    [searchParameters, setSearchParameters]
  );

  const customBaseColumns = useMemo(() => {
    const columns = getBaseColumns();
    const nameColumn = columns.find(col => col.field === 'name');
    if (!nameColumn) return columns;

    const customNameColumn: GridColDef = {
      ...nameColumn,
      renderCell: createComparePageRenderName(handleFolderClick),
    };

    return columns.map(col => (col.field === 'name' ? customNameColumn : col));
  }, [handleFolderClick]);

  return (
    <Box flex={1} minHeight={0} display='flex' flexDirection='column' padding='6px'>
      <ContentsTable
        fetchContents={fetchData}
        currentFolder={folder}
        error={folderError}
        isFolderEmpty={isFolderEmpty}
        isLoading={isFolderLoading || isRootFolderLoading}
        metadataColumns={metadataColumns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        isColumnDataLoading={isMetadataLoading}
        customBaseColumns={customBaseColumns}
        showRootFolder
      />
    </Box>
  );
};
